import { forwardRef } from 'react';

/**
 * Primary button primitive (spec §7.1). Variant and size are carried on
 * `data-*` attributes, never on extra class names, so themes and future
 * variants never require new class names to be threaded through call sites.
 *
 * While `loading` is true the label stays in the DOM — and therefore in the
 * accessible name — but is hidden visually in favour of `.etch-button__spinner`
 * (see buttons.css). This keeps the button's width and its announced name
 * stable across the loading transition.
 *
 * @param {object} props
 * @param {'primary'|'secondary'|'ghost'|'danger'|'signal'} [props.variant='primary']
 * @param {'compact'|'default'|'large'} [props.size='default']
 * @param {boolean} [props.block] - stretch to fill the width of its container.
 * @param {boolean} [props.loading] - shows the spinner, keeps the label accessible.
 * @param {boolean} [props.disabled]
 * @param {'button'|'submit'|'reset'} [props.type='button']
 * @param {import('react').ElementType} [props.as='button'] - render as e.g.
 *   `RouterLink`/`NavLink` for a nav item that must look like a button. Only a
 *   real `<button>` gets `type`/`disabled`; other elements get `aria-disabled`
 *   instead, since a non-form element has no native disabled state.
 * @param {string} [props.className]
 * @param {import('react').ReactNode} props.children
 */
export const Button = forwardRef(function Button(
  {
    as: Component = 'button',
    variant = 'primary',
    size = 'default',
    block = false,
    loading = false,
    disabled = false,
    type = 'button',
    className = '',
    onClick,
    children,
    ...rest
  },
  ref
) {
  // `pointer-events: none` in the stylesheet stops a second mouse click, but not
  // a second Enter press — and we deliberately do not set `disabled` while
  // loading, because that would yank focus out of the button mid-action.
  const handleClick = (event) => {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  const elementProps =
    Component === 'button' ? { type, disabled } : { 'aria-disabled': disabled || undefined };

  return (
    <Component
      ref={ref}
      className={`etch-button ${className}`.trim()}
      data-variant={variant}
      data-size={size === 'default' ? undefined : size}
      data-block={block || undefined}
      data-loading={loading || undefined}
      aria-busy={loading || undefined}
      onClick={handleClick}
      {...elementProps}
      {...rest}
    >
      {children}
      {loading && <span className="etch-button__spinner" aria-hidden="true" />}
    </Component>
  );
});

export default Button;
